import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { getProject } from "@/app/lib/auditflowApi";
import type { AuditFlowDocument, AuditFlowFolder } from "@/app/components/shared/types";

interface Props {
    projectId: string;
    linkedIds: Set<string>;
    onToggle: (doc: AuditFlowDocument, linked: boolean) => void;
    disabled?: boolean;
}

function buildFolderTree(
    folders: AuditFlowFolder[],
    documents: AuditFlowDocument[],
    parentId: string | null,
): { folders: AuditFlowFolder[]; docs: AuditFlowDocument[] } {
    return {
        folders: folders.filter((f) => f.parent_folder_id === parentId),
        docs: documents.filter((d) => (d.folder_id ?? null) === parentId),
    };
}

function FolderSection({
    folder,
    allFolders,
    allDocs,
    linkedIds,
    onToggle,
    disabled,
    depth,
}: {
    folder: AuditFlowFolder;
    allFolders: AuditFlowFolder[];
    allDocs: AuditFlowDocument[];
    linkedIds: Set<string>;
    onToggle: (doc: AuditFlowDocument, linked: boolean) => void;
    disabled?: boolean;
    depth: number;
}) {
    const { folders: children, docs } = buildFolderTree(
        allFolders,
        allDocs,
        folder.id,
    );

    return (
        <div style={{ marginLeft: depth * 12 }} className="space-y-1">
            <p className="text-xs font-medium text-gray-600">{folder.name}</p>
            {docs.map((doc) => (
                <label
                    key={doc.id}
                    className="flex items-start gap-2 text-sm text-gray-500"
                >
                    <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={linkedIds.has(doc.id)}
                        disabled={disabled}
                        onChange={(e) => onToggle(doc, e.target.checked)}
                    />
                    <span className="min-w-0 truncate leading-snug">{doc.filename}</span>
                </label>
            ))}
            {children.map((child) => (
                <FolderSection
                    key={child.id}
                    folder={child}
                    allFolders={allFolders}
                    allDocs={allDocs}
                    linkedIds={linkedIds}
                    onToggle={onToggle}
                    disabled={disabled}
                    depth={depth + 1}
                />
            ))}
        </div>
    );
}

export function LauraProjectDocumentPicker({
    projectId,
    linkedIds,
    onToggle,
    disabled,
}: Props) {
    const [loading, setLoading] = useState(true);
    const [folders, setFolders] = useState<AuditFlowFolder[]>([]);
    const [documents, setDocuments] = useState<AuditFlowDocument[]>([]);

    useEffect(() => {
        setLoading(true);
        getProject(projectId)
            .then((p) => {
                setFolders(p.folders ?? []);
                setDocuments(p.documents ?? []);
            })
            .finally(() => setLoading(false));
    }, [projectId]);

    const root = useMemo(
        () => buildFolderTree(folders, documents, null),
        [folders, documents],
    );

    if (loading) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
        );
    }

    if (documents.length === 0) {
        return <p className="text-sm text-gray-500">No documents in this project.</p>;
    }

    return (
        <div className="max-h-32 overflow-y-auto rounded-lg border bg-gray-50 p-2 space-y-1">
            {root.docs.map((doc) => (
                <label
                    key={doc.id}
                    className="flex items-start gap-2 text-sm text-gray-500"
                >
                    <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={linkedIds.has(doc.id)}
                        disabled={disabled}
                        onChange={(e) => onToggle(doc, e.target.checked)}
                    />
                    <span className="min-w-0 truncate leading-snug">{doc.filename}</span>
                </label>
            ))}
            {root.folders.map((folder) => (
                <FolderSection
                    key={folder.id}
                    folder={folder}
                    allFolders={folders}
                    allDocs={documents}
                    linkedIds={linkedIds}
                    onToggle={onToggle}
                    disabled={disabled}
                    depth={0}
                />
            ))}
        </div>
    );
}
